<template>
  <div class="profile-page">
    <!-- Page Header -->
    <div class="profile-header">
      <div class="header-content">
        <div class="avatar-section">
          <div class="avatar-circle">
            <i class="fas fa-user"></i>
          </div>
        </div>
        <div class="user-info-section">
          <h1 class="user-name">{{ displayUser?.name || username }}</h1>
          <p class="user-email" v-if="isOwnProfile">{{ displayUser?.email }}</p>
          <p class="user-email" v-else>@{{ username }}</p>
          <div class="user-stats-summary">
            <div v-if="isOwnProfile" class="stat-badge">
              <i class="fas fa-star"></i>
              <span>{{ totalStars }} Stars</span>
            </div>
            <div class="stat-badge">
              <i class="fas fa-trophy"></i>
              <span>{{ quizStats?.stats?.quizzes_taken || 0 }} Quizzes</span>
            </div>
            <div class="stat-badge">
              <i class="fas fa-award"></i>
              <span>{{ badgeCount }} Badges</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading your profile...</p>
    </div>

    <!-- Content Sections -->
    <div v-else class="profile-content">
      <!-- Edit Username Section (own profile only) -->
      <section v-if="isOwnProfile" class="profile-section">
        <div class="section-header">
          <h2><i class="fas fa-pen"></i> Edit Username</h2>
        </div>
        <div class="edit-username-form">
          <div class="input-group">
            <input
              v-model="newUsername"
              type="text"
              placeholder="New username (lowercase, numbers, dashes)"
              class="username-input"
              :class="{ 'input-error': usernameError }"
              @input="validateNewUsername"
            />
            <button
              @click="changeUsername"
              :disabled="!canSubmitUsername"
              class="save-username-btn"
            >
              <i v-if="savingUsername" class="fas fa-spinner fa-spin"></i>
              <template v-else>Save</template>
            </button>
          </div>
          <p v-if="usernameError" class="field-error">{{ usernameError }}</p>
          <p v-if="usernameSuccess" class="field-success">{{ usernameSuccess }}</p>
          <p v-else-if="!usernameError" class="field-hint">3-30 characters: lowercase letters, numbers, and dashes only</p>
        </div>
      </section>

      <!-- Achievements Section -->
      <section class="profile-section">
        <div class="section-header">
          <h2><i class="fas fa-award"></i> Badges & Achievements</h2>
        </div>

        <!-- Global Title -->
        <div v-if="quizStats?.achievements?.titles?.global" class="global-title-card">
          <div class="title-icon">👑</div>
          <div class="title-details">
            <h3>{{ quizStats.achievements.titles.global }}</h3>
            <p>Global Achievement</p>
          </div>
        </div>

        <!-- Badges -->
        <div v-if="quizStats?.achievements?.badges?.length > 0" class="achievements-grid">
          <div
            v-for="(badge, index) in quizStats.achievements.badges"
            :key="index"
            class="achievement-card"
          >
            <div class="achievement-icon">{{ getBadgeIcon(badge) }}</div>
            <div class="achievement-details">
              <h3>{{ badge }}</h3>
              <p class="achievement-date">Badge Earned</p>
            </div>
          </div>
        </div>

        <!-- Country Titles -->
        <div v-if="quizStats?.achievements?.titles?.countries?.length > 0" class="country-titles">
          <h3 class="subsection-title">Country Achievements</h3>
          <div class="achievements-grid">
            <div
              v-for="countryTitle in quizStats.achievements.titles.countries"
              :key="countryTitle.country_code"
              class="achievement-card country-card"
            >
              <div class="achievement-icon">{{ getCountryFlag(countryTitle.country_code) }}</div>
              <div class="achievement-details">
                <h3>{{ countryTitle.title }}</h3>
                <p class="achievement-date">{{ getCountryName(countryTitle.country_code) }}</p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!quizStats?.achievements?.badges?.length && !quizStats?.achievements?.titles?.global" class="empty-state">
          <i class="fas fa-trophy empty-icon"></i>
          <p>No badges yet. Complete quizzes to earn achievements!</p>
        </div>
      </section>

      <!-- Quiz Stats Section -->
      <section class="profile-section">
        <div class="section-header">
          <h2><i class="fas fa-chart-line"></i> Quiz Statistics</h2>
        </div>
        <div v-if="quizStats" class="stats-grid">
          <div class="stat-card">
            <div class="stat-icon">🎯</div>
            <div class="stat-value">{{ quizStats.total_score || 0 }}</div>
            <div class="stat-label">Total Score</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">✅</div>
            <div class="stat-value">{{ quizStats.stats?.quizzes_taken || 0 }}</div>
            <div class="stat-label">Quizzes Taken</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">🎯</div>
            <div class="stat-value">{{ formatAccuracy(quizStats.stats?.average_accuracy) }}</div>
            <div class="stat-label">Avg Accuracy</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">⚡</div>
            <div class="stat-value">{{ formatTime(quizStats.stats?.average_time_per_question) }}</div>
            <div class="stat-label">Avg Time</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">🔥</div>
            <div class="stat-value">{{ quizStats.streak?.current || 0 }}</div>
            <div class="stat-label">Current Streak</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">🏆</div>
            <div class="stat-value">{{ quizStats.streak?.longest || 0 }}</div>
            <div class="stat-label">Longest Streak</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">🌍</div>
            <div class="stat-value">{{ quizStats.country_ranks?.length || 0 }}</div>
            <div class="stat-label">Countries Explored</div>
          </div>
        </div>
        <div v-else class="empty-state">
          <i class="fas fa-question-circle empty-icon"></i>
          <p>No quiz data yet. Start exploring to see your stats!</p>
        </div>
      </section>

      <!-- Starred Places Section (only show on own profile) -->
      <section v-if="isOwnProfile" class="profile-section">
        <div class="section-header">
          <h2><i class="fas fa-star"></i> Starred Places</h2>
        </div>
        <div v-if="starredPlaces.length > 0" class="starred-grid">
          <div
            v-for="place in starredPlaces"
            :key="place.id"
            class="starred-card"
            @click="navigateToPlace(place)"
          >
            <div class="starred-header">
              <h3>{{ place.name?.city || 'Unknown Place' }}</h3>
              <span class="star-icon">⭐</span>
            </div>
            <p class="starred-location">
              <i class="fas fa-map-marker-alt"></i>
              {{ formatPlaceLocation(place) }}
            </p>
            <p class="starred-date">
              Starred {{ formatDate(place.starred_at) }}
            </p>
          </div>
        </div>
        <div v-else class="empty-state">
          <i class="fas fa-star empty-icon"></i>
          <p>No starred places yet. Explore the map and star your favorites!</p>
        </div>
      </section>

      <!-- Starred Articles Section (only show on own profile) -->
      <section v-if="isOwnProfile" class="profile-section">
        <div class="section-header">
          <h2><i class="fas fa-newspaper"></i> Starred Articles</h2>
        </div>
        <div v-if="starredArticles.length > 0" class="starred-grid">
          <div
            v-for="article in starredArticles"
            :key="article.id"
            class="starred-card"
            @click="navigateToArticle(article)"
          >
            <div class="starred-header">
              <h3>{{ article.title || 'Untitled Article' }}</h3>
              <span class="star-icon">⭐</span>
            </div>
            <p class="article-intro">
              <i class="fas fa-bookmark"></i>
              {{ article.categorySlug || 'Article' }}
            </p>
            <p class="starred-date">
              Starred {{ formatDate(article.starred_at) }}
            </p>
          </div>
        </div>
        <div v-else class="empty-state">
          <i class="fas fa-newspaper empty-icon"></i>
          <p>No starred articles yet. Read and star interesting articles!</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'ProfilePage',

  props: {
    username: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loading: true,
      starredPlaces: [],
      starredArticles: [],
      quizStats: null,
      profileUser: null,
      newUsername: '',
      usernameError: null,
      usernameSuccess: null,
      savingUsername: false
    };
  },

  computed: {
    ...mapGetters('auth', ['user', 'isAuthenticated']),

    totalStars() {
      return this.starredPlaces.length + this.starredArticles.length;
    },

    badgeCount() {
      const badges = this.quizStats?.achievements?.badges?.length || 0;
      const titles = this.quizStats?.achievements?.titles?.countries?.length || 0;
      const globalTitle = this.quizStats?.achievements?.titles?.global ? 1 : 0;
      return badges + titles + globalTitle;
    },

    isOwnProfile() {
      return this.isAuthenticated && this.user?.name === this.username;
    },

    canSubmitUsername() {
      return this.newUsername.trim() &&
        !this.usernameError &&
        !this.savingUsername &&
        this.newUsername !== this.user?.name;
    },

    displayUser() {
      // Use profileUser for display data (works for both own and others' profiles)
      return this.profileUser || this.user;
    }
  },

  async mounted() {
    await this.loadProfileData();
  },

  watch: {
    // Reload data when username changes
    username() {
      this.loadProfileData();
    }
  },

  methods: {
    async loadProfileData() {
      this.loading = true;

      try {
        // First, get the user ID from the username
        await this.fetchUserByUsername();

        if (!this.profileUser) {
          console.error('User not found');
          this.loading = false;
          return;
        }

        // Fetch starred items (only if viewing own profile or if authenticated)
        if (this.isOwnProfile) {
          await this.fetchStarredItems();
        }

        // Fetch quiz stats and achievements
        await this.fetchQuizStats();
      } catch (error) {
        console.error('Error loading profile data:', error);
      } finally {
        this.loading = false;
      }
    },

    async fetchUserByUsername() {
      try {
        // For now, if it's the current user, use the stored user data
        if (this.isAuthenticated && this.user?.name === this.username) {
          this.profileUser = this.user;
          return;
        }

        // Otherwise, we'd need an API endpoint to fetch user by username
        // For now, we'll use the quiz-stats endpoint which returns username
        // This is a workaround - ideally you'd have a /api/users/:username endpoint
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/users/search?username=${this.username}`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            this.profileUser = data.user;
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    },

    async fetchStarredItems() {
      try {
        const token = localStorage.getItem('auth-token');
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/users/me/stars`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Stars response:', data);

        if (data.success) {
          this.starredPlaces = data.data.starred_places || [];
          this.starredArticles = data.data.starred_articles || [];
          console.log('Starred places:', this.starredPlaces);
          console.log('Starred articles:', this.starredArticles);
        }
      } catch (error) {
        console.error('Error fetching starred items:', error);
      }
    },

    async fetchQuizStats() {
      try {
        // Use profileUser ID if available, otherwise try to get by username
        let userId = this.profileUser?.id;

        if (!userId && this.quizStats?.user_id) {
          userId = this.quizStats.user_id;
        }

        if (!userId) {
          console.warn('No user ID available for quiz stats');
          return;
        }

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/users/${userId}/quiz-stats`
        );

        const data = await response.json();

        if (data.success) {
          this.quizStats = data.data;
          // If we didn't have profile user data, extract it from quiz stats
          if (!this.profileUser && data.data.username) {
            this.profileUser = {
              name: data.data.username,
              id: data.data.user_id
            };
          }
        }
      } catch (error) {
        console.error('Error fetching quiz stats:', error);
      }
    },

    getBadgeIcon(badgeName) {
      const icons = {
        'Speed Demon': '⚡',
        'Perfect 10': '⭐',
        'Quiz Master': '🎓',
        'World Explorer': '🌍',
        'Streak King': '🔥',
        'Geography Guru': '📚'
      };
      return icons[badgeName] || '🏆';
    },

    getCountryFlag(countryCode) {
      if (!countryCode) return '🌍';
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt());
      return String.fromCodePoint(...codePoints);
    },

    getCountryName(countryCode) {
      const countries = {
        'MX': 'Mexico',
        'GB': 'United Kingdom',
        'NO': 'Norway',
        'PE': 'Peru',
        'ID': 'Indonesia',
        'KH': 'Cambodia',
        'GR': 'Greece',
        'FR': 'France',
        'US': 'United States',
        'ES': 'Spain',
        'IT': 'Italy',
        'JP': 'Japan',
        'CN': 'China',
        'BR': 'Brazil',
        'AU': 'Australia'
      };
      return countries[countryCode] || countryCode;
    },

    formatAccuracy(accuracy) {
      if (!accuracy) return '0%';
      return `${Math.round(accuracy)}%`;
    },

    formatTime(seconds) {
      if (!seconds) return '0s';
      return `${seconds.toFixed(1)}s`;
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const now = new Date();
      const diffTime = Math.abs(now - d);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return d.toLocaleDateString();
    },

    formatPlaceLocation(place) {
      if (!place) return 'Unknown';
      const parts = [];
      if (place.name?.region) parts.push(place.name.region);
      if (place.name?.country) parts.push(place.name.country);
      return parts.join(', ') || 'Location unavailable';
    },

    navigateToPlace(place) {
      if (place.id) {
        // Navigate to map and open place info
        this.$router.push(`/?place=${place.id}`);
      }
    },

    navigateToArticle(article) {
      if (article.slug) {
        // Navigate to article
        this.$router.push(`/article/${article.slug}`);
      }
    },

    validateNewUsername() {
      this.usernameSuccess = null;
      const name = this.newUsername.trim();

      if (!name) {
        this.usernameError = null;
        return;
      }
      if (!/^[a-z0-9-]+$/.test(name)) {
        this.usernameError = 'Only lowercase letters, numbers, and dashes allowed';
        return;
      }
      if (name.length < 3) {
        this.usernameError = 'Username must be at least 3 characters';
        return;
      }
      if (name.length > 30) {
        this.usernameError = 'Username must be 30 characters or less';
        return;
      }
      if (name === this.user?.name) {
        this.usernameError = 'This is already your username';
        return;
      }
      this.usernameError = null;
    },

    async changeUsername() {
      if (!this.canSubmitUsername) return;

      this.savingUsername = true;
      this.usernameSuccess = null;

      try {
        const token = localStorage.getItem('auth-token');
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/auth/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ name: this.newUsername.trim() })
        });

        const data = await response.json();

        if (!response.ok) {
          this.usernameError = data.error || 'Failed to update username';
          return;
        }

        // Update store
        this.$store.commit('auth/SET_USER', data.user);

        this.usernameSuccess = 'Username updated successfully!';
        this.newUsername = '';

        // Navigate to new profile URL
        this.$router.replace(`/profile/${data.user.name}`);
      } catch (error) {
        this.usernameError = 'Something went wrong. Please try again.';
      } finally {
        this.savingUsername = false;
      }
    }
  }
};
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding-bottom: 40px;
}

/* Header */
.profile-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 40px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 30px;
}

.avatar-section .avatar-circle {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 3px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: white;
}

.user-info-section {
  flex: 1;
}

.user-name {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.user-email {
  font-size: 16px;
  opacity: 0.9;
  margin: 0 0 20px 0;
}

.user-stats-summary {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.stat-badge {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
}

.stat-badge i {
  color: #FFD700;
}

/* Loading */
.loading-container {
  max-width: 1200px;
  margin: 40px auto;
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(102, 126, 234, 0.2);
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Content */
.profile-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.profile-section {
  background: white;
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.section-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
}

.section-header h2 {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-header i {
  color: #667eea;
}

/* Global Title Card */
.global-title-card {
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  box-shadow: 0 8px 24px rgba(255, 215, 0, 0.3);
  transition: transform 0.3s ease;
}

.global-title-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(255, 215, 0, 0.4);
}

.title-icon {
  font-size: 56px;
  line-height: 1;
}

.title-details h3 {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}

.title-details p {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: 600;
}

/* Country Titles Section */
.country-titles {
  margin-top: 32px;
}

.subsection-title {
  font-size: 18px;
  font-weight: 600;
  color: #475569;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #e2e8f0;
}

/* Achievements Grid */
.achievements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.achievement-card {
  background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
  border: 2px solid #667eea30;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.achievement-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.2);
}

.achievement-icon {
  font-size: 48px;
  line-height: 1;
}

.achievement-details h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.achievement-date {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
}

.stat-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  font-size: 36px;
  margin-bottom: 12px;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

/* Starred Items Grid */
.starred-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.starred-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.starred-card:hover {
  border-color: #667eea;
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
}

.starred-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 12px;
}

.starred-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  flex: 1;
}

.star-icon {
  font-size: 20px;
  filter: drop-shadow(0 0 4px rgba(255, 215, 0, 0.5));
}

.starred-location {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.article-intro {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: capitalize;
}

.article-intro i {
  color: #667eea;
  font-size: 12px;
}

.starred-date {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

/* Edit Username */
.edit-username-form {
  max-width: 500px;
}

.input-group {
  display: flex;
  gap: 12px;
}

.username-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  outline: none;
}

.username-input:focus {
  border-color: #667eea;
}

.username-input.input-error {
  border-color: #ef4444;
}

.save-username-btn {
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.save-username-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.save-username-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.field-error {
  color: #ef4444;
  font-size: 13px;
  margin: 8px 0 0;
}

.field-success {
  color: #10b981;
  font-size: 13px;
  margin: 8px 0 0;
  font-weight: 500;
}

.field-hint {
  color: #94a3b8;
  font-size: 13px;
  margin: 8px 0 0;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #94a3b8;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .profile-header {
    padding: 24px 16px;
  }

  .header-content {
    flex-direction: column;
    text-align: center;
  }

  .avatar-circle {
    width: 80px !important;
    height: 80px !important;
    font-size: 32px !important;
  }

  .user-name {
    font-size: 24px;
  }

  .user-stats-summary {
    justify-content: center;
  }

  .profile-content {
    padding: 20px 16px;
  }

  .profile-section {
    padding: 20px;
  }

  .section-header h2 {
    font-size: 20px;
  }

  .achievements-grid,
  .stats-grid,
  .starred-grid {
    grid-template-columns: 1fr;
  }
}
</style>
